import { createToken } from '../container/index';

// ─── Service Interfaces ────────────────────────────────────────────────────────
// Define contracts here. Concrete implementations live in each app.
// This allows the Capacitor mobile app to swap in native implementations
// (e.g. @capacitor/preferences for StorageService).

/**
 * Tracks user events and interactions.
 * Web impl: Sentry breadcrumbs / PostHog / etc.
 * Mobile impl: same or Capacitor-compatible client.
 */
export interface AnalyticsService {
  track(event: string, properties?: Record<string, unknown>): void;
  identify(userId: string, traits?: Record<string, unknown>): void;
  page(name: string, properties?: Record<string, unknown>): void;
  reset(): void;
}

/**
 * Persistent key-value storage.
 * Web impl: localStorage.
 * Mobile impl: @capacitor/preferences.
 */
export interface StorageService {
  get<T>(key: string): Promise<T | null>;
  set<T>(key: string, value: T): Promise<void>;
  remove(key: string): Promise<void>;
  clear(): Promise<void>;
}

/**
 * Structured application logging with error capture.
 * Web impl: console + Sentry.
 * Mobile impl: same (Sentry supports Capacitor).
 */
export interface LoggingService {
  info(message: string, context?: Record<string, unknown>): void;
  warn(message: string, context?: Record<string, unknown>): void;
  error(message: string, error?: Error, context?: Record<string, unknown>): void;
}

/**
 * Platform notification service.
 * Web impl: Notification API / toast.
 * Mobile impl: @capacitor/local-notifications.
 */
export interface NotificationService {
  requestPermission(): Promise<boolean>;
  show(title: string, body: string, options?: NotificationOptions): Promise<void>;
}

interface NotificationOptions {
  icon?: string;
  tag?: string;
  data?: unknown;
}

// ─── DI Tokens ────────────────────────────────────────────────────────────────

export const TOKENS = {
  Analytics: createToken<AnalyticsService>('AnalyticsService'),
  Storage: createToken<StorageService>('StorageService'),
  Logging: createToken<LoggingService>('LoggingService'),
  Notification: createToken<NotificationService>('NotificationService'),
} as const;
